import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Checkbox, ErrorNote, Field, Input, Notice, Tabs } from '../../components/ui';
import { api, clearStoredToken, errorMessage } from '../../lib/api';
import { NEW_PIN_DIGITS, newPinProblem, readSetupLink } from '../../lib/appSetup';
import { FullPageSpinner, useCustomerMe } from '../../lib/auth';
import { PinInput } from './mobile';

type Mode = 'signin' | 'register' | 'code';

/**
 * Customer sign-in. With OTP enabled: mobile → OTP. Otherwise: mobile + PIN, new registration with PIN,
 * or a one-time setup code from the reception for customers registered there. The reception's WhatsApp
 * message, printed slip and on-screen QR open /app/login?setup=<mobile>#code=<code>: the Setup code tab with both
 * filled in, so the customer only chooses a 4-digit PIN.
 */
export default function CustomerLogin() {
  const { data: me, isLoading } = useCustomerMe();
  const {
    data: cfg,
    isLoading: cfgLoading,
    isError: cfgError,
    error: cfgErr,
    refetch: refetchCfg,
  } = useQuery({ queryKey: ['customer-config'], queryFn: () => api<{ otpLogin: boolean; business: { name: string } }>('/customer/config') });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from the reception's link: straight to "Setup code", with the mobile and the code filled in.
  const [fromLink] = useState(() => readSetupLink(location.search, location.hash));
  const [mode, setMode] = useState<Mode>(fromLink ? 'code' : 'signin');
  const [mobile, setMobile] = useState(fromLink?.mobile ?? '');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState(fromLink?.code ?? '');
  const [setupCode, setSetupCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [name, setName] = useState('');
  const [place, setPlace] = useState('');
  const [consent, setConsent] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const done = async () => {
    clearStoredToken();
    await qc.invalidateQueries({ queryKey: ['customer-me'] });
    navigate((location.state as { from?: string } | null)?.from ?? '/app', { replace: true });
  };

  const requestOtp = useMutation({ mutationFn: () => api('/customer/auth/otp/request', { body: { mobile } }), onSuccess: () => setOtpSent(true) });
  const verify = useMutation({
    mutationFn: () =>
      api('/customer/auth/otp/verify', { body: { mobile, code, pin: pin || undefined, name: name || undefined, place: place || undefined, marketingConsent: consent } }),
    onSuccess: done,
  });
  const login = useMutation({ mutationFn: () => api('/customer/auth/login', { body: { mobile, pin } }), onSuccess: done });
  const register = useMutation({
    mutationFn: () => api('/customer/auth/register', { body: { mobile, pin, name, place: place || undefined, marketingConsent: consent, setupCode: setupCode || undefined } }),
    onSuccess: done,
  });

  if (isLoading || cfgLoading) return <FullPageSpinner />;
  if (cfgError) {
    return (
      <div className="cust flex min-h-dvh flex-col items-center justify-center bg-shell p-6 text-center text-slate-800">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">!</div>
          <h2 className="text-lg font-bold text-slate-900">Unable to load app config</h2>
          <p className="text-sm text-slate-600">{errorMessage(cfgErr)}</p>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => refetchCfg()}
              className="h-12 cursor-pointer rounded-lg bg-brand-600 px-6 text-base font-medium text-white hover:bg-brand-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!cfg) return <FullPageSpinner />;
  if (me) return <Navigate to="/app" replace />;

  const mobileField = (
    <Field label="Mobile number">
      {(id) => (
        <Input id={id} type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next" placeholder="98765 43210" className="tabular" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
      )}
    </Field>
  );
  // Signing in takes the PIN chosen before (4 digits; older ones may have 6); a new PIN has exactly 4 digits.
  const pinField = (label: string, fresh = false) => (
    <Field label={label} hint={fresh ? `${NEW_PIN_DIGITS} digits you will remember. Staff never ask for it.` : 'The PIN you chose'} error={fresh ? pinError : null}>
      {(id) => (
        <PinInput
          id={id}
          autoComplete={fresh ? 'new-password' : 'current-password'}
          maxLength={fresh ? NEW_PIN_DIGITS : 6}
          value={pin}
          onChange={(v) => {
            setPin(v);
            setPinError(null);
          }}
          required
        />
      )}
    </Field>
  );
  const profileFields = (
    <>
      <Field label="Your name">{(id) => <Input id={id} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
      <Field label="Place (town / area)">{(id) => <Input id={id} autoComplete="address-level2" value={place} onChange={(e) => setPlace(e.target.value)} />}</Field>
      <Checkbox label="Send me offers and event updates" description="You can turn this off any time in your profile." checked={consent} onChange={setConsent} />
    </>
  );

  return (
    <div className="cust flex min-h-dvh flex-col bg-shell pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(2rem,env(safe-area-inset-top))]">
      <div className="mx-auto w-full max-w-md">
        <p className="break-words text-3xl font-bold tracking-tight text-white">
          {cfg.business.name}
        </p>
        <p className="mb-6 mt-1 text-sm text-slate-300">Your wallet, rewards and visits at {cfg.business.name}</p>
        <div className="rounded-2xl bg-white p-4 shadow-xl min-[400px]:p-5">
          {cfg.otpLogin ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                otpSent ? verify.mutate() : requestOtp.mutate();
              }}
            >
              {mobileField}
              {otpSent && (
                <>
                  <Field label="6-digit code" hint="Sent by SMS to your mobile">
                    {(id) => (
                      <Input
                        id={id}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        maxLength={6}
                        className="h-14 text-center text-2xl tracking-[0.4em] tabular lg:h-14"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        required
                      />
                    )}
                  </Field>
                  <details className="text-sm">
                    <summary className="flex min-h-11 cursor-pointer items-center text-slate-600">New here? Add your details</summary>
                    <div className="mt-3 flex flex-col gap-3">{profileFields}</div>
                  </details>
                </>
              )}
              <ErrorNote error={requestOtp.error || verify.error} />
              <Button type="submit" size="lg" className="h-14 w-full text-lg" loading={requestOtp.isPending || verify.isPending}>
                {otpSent ? 'Verify & continue' : 'Send code'}
              </Button>
            </form>
          ) : (
            <>
              <Tabs<Mode>
                value={mode}
                onChange={setMode}
                tabs={[
                  { value: 'signin', label: 'Sign in' },
                  { value: 'register', label: 'Register' },
                  { value: 'code', label: 'Setup code' },
                ]}
              />
              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode === 'signin') return login.mutate();
                  const problem = newPinProblem(pin);
                  if (problem) return setPinError(problem);
                  if (mode === 'register') register.mutate();
                  else verify.mutate();
                }}
              >
                {mobileField}
                {mode === 'signin' && (
                  <>
                    {pinField('PIN')}
                    <p className="-mt-2 text-sm text-slate-600">
                      Forgot your PIN?{' '}
                      <button
                        type="button"
                        className="inline-flex min-h-11 items-center text-left font-medium text-brand-700 underline"
                        onClick={() => {
                          setPin('');
                          setMode('code');
                        }}
                      >
                        Use a setup code from the reception
                      </button>
                    </p>
                  </>
                )}
                {mode === 'register' && (
                  <>
                    {profileFields}
                    <Field label="Setup code from the counter" hint="6 digits. Ask the counter or the gate for it: it proves this mobile is yours.">
                      {(id) => (
                        <Input
                          id={id}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          maxLength={6}
                          className="h-14 text-center text-2xl tracking-[0.4em] tabular lg:h-14"
                          value={setupCode}
                          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                          required
                        />
                      )}
                    </Field>
                    {pinField('Choose a PIN', true)}
                  </>
                )}
                {mode === 'code' && (
                  <>
                    <Notice>
                      {fromLink?.code
                        ? 'Your mobile number and setup code are filled in. Check them, then choose a 4-digit PIN.'
                        : 'Got a setup code from the reception? Enter your mobile number and the code, then choose a 4-digit PIN. It also resets a forgotten PIN.'}
                    </Notice>
                    <Field label="Setup code" hint="6 digits, from the reception (on WhatsApp, the slip or their screen)">
                      {(id) => (
                        <Input
                          id={id}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          maxLength={6}
                          className="h-14 text-center text-2xl tracking-[0.4em] tabular lg:h-14"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                          required
                        />
                      )}
                    </Field>
                    {pinField('Choose a PIN', true)}
                  </>
                )}
                <ErrorNote error={login.error || register.error || verify.error} />
                <Button type="submit" size="lg" className="h-14 w-full text-lg" loading={login.isPending || register.isPending || verify.isPending}>
                  {mode === 'signin' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Activate'}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-slate-400">
          Staff?{' '}
          <a className="tap px-1 underline" href="/admin/login">
            Sign in here
          </a>
        </p>
      </div>
    </div>
  );
}
